import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useSettings } from '../../context/SettingsContext';
import { normalizePrintingSettings, normalizeSettings } from '../../lib/printing';
import { discoverPrinterServer, printTest } from '../../services/printService';
import { isPrintStationEnabled, setPrintStationEnabled } from '../../lib/printStation';
import {
  authorizeOfflineDevice, disableOfflineDevice, getCachedOfflineDeviceAuthorization,
  refreshOfflineDeviceAuthorization,
} from '../../lib/offlineDevice';
import { useOfflineSync } from '../../context/OfflineSyncContext';

const currencies = ['COP', 'CLP', 'USD', 'MXN', 'EUR'];

const GlobalSettingsView = ({ onBack }) => {
  const { settings, loadingSettings, savingSettings, saveSettings } = useSettings();
  const { counts: offlineCounts } = useOfflineSync();
  const [localSettings, setLocalSettings] = useState(normalizeSettings(settings));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testingPrint, setTestingPrint] = useState(false);
  const [discoveringPrinter, setDiscoveringPrinter] = useState(false);
  const [printerDiscoveryStatus, setPrinterDiscoveryStatus] = useState('');
  const [localPrintStation, setLocalPrintStation] = useState(isPrintStationEnabled);
  const [offlineDevice, setOfflineDevice] = useState(getCachedOfflineDeviceAuthorization);
  const [savingOfflineDevice, setSavingOfflineDevice] = useState(false);

  useEffect(() => {
    setLocalSettings(normalizeSettings(settings));
  }, [settings]);

  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    refreshOfflineDeviceAuthorization()
      .then(setOfflineDevice)
      .catch(() => setOfflineDevice(getCachedOfflineDeviceAuthorization()));
  }, []);

  const handleOfflineDeviceChange = async (enabled) => {
    if (!enabled && offlineCounts.total > 0) {
      toast.warn('Sincroniza o revisa todas las operaciones pendientes antes de desautorizar este PC.');
      return;
    }
    setSavingOfflineDevice(true);
    try {
      let device = null;
      if (enabled) {
        try {
          device = await authorizeOfflineDevice({ label: localSettings?.storeName || 'PC principal' });
        } catch (err) {
          if (err?.code !== 'OFFLINE_DEVICE_REPLACE_REQUIRED') throw err;
          const replace = window.confirm(
            'Ya existe otro computador principal. Antes de reemplazarlo confirma que allí no queden operaciones pendientes. ¿Deseas desautorizarlo y usar este PC en su lugar?'
          );
          if (!replace) return;
          device = await authorizeOfflineDevice({
            label: localSettings?.storeName || 'PC principal',
            replace: true,
          });
        }
        toast.success('Este PC quedó autorizado para trabajar sin internet.');
      } else {
        await disableOfflineDevice();
        toast.info('Este PC dejó de estar autorizado para trabajo offline.');
      }
      setOfflineDevice(device);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'No se pudo cambiar la autorización de este PC.');
    } finally {
      setSavingOfflineDevice(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleTaxModeChange = (value) => {
    setLocalSettings((prev) => ({ ...prev, taxEnabled: value === 'enabled' }));
  };

  const handlePrintingChange = (name, value) => {
    setLocalSettings((prev) => ({
      ...prev,
      printing: {
        ...normalizePrintingSettings(prev?.printing),
        [name]: value,
      },
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024 * 2) {
      setError('El logo debe pesar menos de 2MB.');
      toast.error('El logo debe pesar menos de 2MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setLocalSettings((prev) => ({ ...prev, storeLogo: event.target?.result || '' }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveSettings = async () => {
    setError('');
    setSuccess('');
    try {
      await saveSettings(localSettings);
      setSuccess('Configuracion guardada exitosamente.');
      toast.success('Configuracion guardada.');
    } catch (err) {
      console.error(err);
      setError('No se pudo guardar la configuracion. Intenta de nuevo.');
      toast.error('No se pudo guardar la configuracion.');
    }
  };

  const handlePrintTest = async () => {
    setTestingPrint(true);
    try {
      const printConfig = localPrintStation
        ? {
            ...localSettings?.printing,
            printingEnabled: true,
            printingMode: 'server',
            printerServerUrl: 'http://localhost:3001',
            fallbackToBrowserPrint: false,
          }
        : localSettings?.printing;
      await printTest(printConfig, {
        storeName: localSettings?.ticketDisplayName || localSettings?.storeName || 'ValenPOS',
        address: localSettings?.address || '',
        phone: localSettings?.phone || '',
        footer: 'Prueba de impresion',
      });
      toast.success('Prueba de impresion enviada.');
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'No se pudo imprimir la prueba.');
    } finally {
      setTestingPrint(false);
    }
  };

  const handleDiscoverPrinter = async () => {
    setDiscoveringPrinter(true);
    setPrinterDiscoveryStatus('');
    try {
      const found = await discoverPrinterServer(
        localSettings?.printing?.printerServerUrl,
        setPrinterDiscoveryStatus
      );
      const nextSettings = {
        ...localSettings,
        printing: {
          ...normalizePrintingSettings(localSettings?.printing),
          printerServerUrl: found.url,
          printerName: found.health?.printerName || localSettings?.printing?.printerName || '',
          printingEnabled: true,
          printingMode: 'server',
        },
      };
      setLocalSettings(nextSettings);
      await saveSettings(nextSettings);
      setPrinterDiscoveryStatus(`Servidor encontrado: ${found.url} · ${found.health?.printerName || 'Impresora configurada'}`);
      toast.success('Printer-server encontrado y configuración guardada.');
    } catch (err) {
      console.error(err);
      setPrinterDiscoveryStatus(err?.message || 'No se encontró el printer-server.');
      toast.error(err?.message || 'No se encontró el printer-server.');
    } finally {
      setDiscoveringPrinter(false);
    }
  };

  if (loadingSettings) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Cargando configuracion...</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-400"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menu
        </button>
        <h1 className="text-4xl font-extrabold text-gray-900 drop-shadow-sm">Configuracion Global</h1>
        <div className="w-32" />
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Ajustes Generales del Sistema</h2>
        {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        {success && <div className="mb-4 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</div>}

        <form>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la Tienda</label>
              <input
                type="text"
                name="storeName"
                value={localSettings.storeName || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Moneda Predeterminada</label>
              <select
                name="currency"
                value={localSettings.currency || 'COP'}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              >
                {currencies.map((curr) => (
                  <option key={curr} value={curr}>
                    {curr}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre en los Tickets</label>
              <input
                type="text"
                name="ticketDisplayName"
                value={localSettings.ticketDisplayName || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Modo de Impuesto</label>
              <select
                value={localSettings.taxEnabled ? 'enabled' : 'disabled'}
                onChange={(e) => handleTaxModeChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              >
                <option value="enabled">Cobrar impuesto</option>
                <option value="disabled">Sin impuesto</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Impuesto Predeterminado (%)</label>
              <input
                type="number"
                name="taxRate"
                value={localSettings.taxRate ?? 0}
                onChange={handleChange}
                min="0"
                step="0.01"
                disabled={!localSettings.taxEnabled}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Direccion de la Tienda</label>
              <input
                type="text"
                name="address"
                value={localSettings.address || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
              <input
                type="text"
                name="phone"
                value={localSettings.phone || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email de Contacto</label>
              <input
                type="email"
                name="email"
                value={localSettings.email || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo de la Tienda</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition duration-200"
              />
              {localSettings.storeLogo && (
                <div className="mt-3 flex justify-center">
                  <img src={localSettings.storeLogo} alt="Logo de la Tienda" className="h-32 object-contain border rounded-lg" />
                </div>
              )}
            </div>

            <div className="md:col-span-2 rounded-xl border border-blue-200 bg-blue-50 p-5">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={Boolean(offlineDevice?.active)}
                  disabled={savingOfflineDevice}
                  onChange={(event) => void handleOfflineDeviceChange(event.target.checked)}
                />
                <span>
                  <span className="block text-base font-semibold text-blue-950">
                    Autorizar este PC como equipo principal offline
                  </span>
                  <span className="mt-1 block text-sm text-blue-800">
                    Permite que Cajero, Administrador o TI abran caja y vendan cuando no haya internet.
                    Solo puede existir un computador principal autorizado por sede.
                  </span>
                  {savingOfflineDevice && (
                    <span className="mt-2 block text-xs font-semibold text-blue-700">Verificando autorización…</span>
                  )}
                </span>
              </label>
            </div>

            <div className="md:col-span-2 rounded-xl border border-gray-200 bg-gray-50 p-5">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Configuracion de impresion</h3>
                  <p className="text-sm text-gray-500">Controla impresora, modo de impresion y fallback del navegador.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleDiscoverPrinter}
                    disabled={discoveringPrinter || testingPrint || savingSettings}
                    className="rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:border-emerald-400 hover:text-emerald-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {discoveringPrinter ? 'Buscando...' : 'Buscar printer-server'}
                  </button>
                  <button
                    type="button"
                    onClick={handlePrintTest}
                    disabled={testingPrint || discoveringPrinter}
                    className="rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-600 hover:border-blue-400 hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {testingPrint ? 'Probando...' : 'Probar impresion'}
                  </button>
                </div>
              </div>

              {printerDiscoveryStatus && (
                <div className="mb-4 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                  {printerDiscoveryStatus}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="flex items-center gap-3 rounded-lg bg-white px-4 py-3 border border-gray-200">
                  <input
                    type="checkbox"
                    checked={Boolean(localSettings?.printing?.printingEnabled)}
                    onChange={(e) => handlePrintingChange('printingEnabled', e.target.checked)}
                  />
                  <span className="text-sm font-medium text-gray-700">Activar impresion</span>
                </label>

                <label className="flex items-center gap-3 rounded-lg bg-white px-4 py-3 border border-gray-200">
                  <input
                    type="checkbox"
                    checked={Boolean(localSettings?.printing?.fallbackToBrowserPrint)}
                    onChange={(e) => handlePrintingChange('fallbackToBrowserPrint', e.target.checked)}
                  />
                  <span className="text-sm font-medium text-gray-700">Fallback a navegador</span>
                </label>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modo de impresion</label>
                  <select
                    value={localSettings?.printing?.printingMode || 'browser'}
                    onChange={(e) => handlePrintingChange('printingMode', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  >
                    <option value="browser">Browser</option>
                    <option value="server">Server</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ancho de papel</label>
                  <select
                    value={Number(localSettings?.printing?.paperWidth || 80)}
                    onChange={(e) => handlePrintingChange('paperWidth', Number(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  >
                    <option value={80}>80 mm</option>
                    <option value={58}>58 mm</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL del printer-server</label>
                  <input
                    type="text"
                    value={localSettings?.printing?.printerServerUrl || ''}
                    onChange={(e) => handlePrintingChange('printerServerUrl', e.target.value)}
                    placeholder="http://192.168.1.50:3001"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre referencial de la impresora</label>
                  <input
                    type="text"
                    value={localSettings?.printing?.printerName || ''}
                    onChange={(e) => handlePrintingChange('printerName', e.target.value)}
                    placeholder="SAT caja USB"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  />
                </div>

                <div className="md:col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  En modo Windows local, la impresora real se configura en el servidor local con la variable
                  <code className="mx-1 rounded bg-white px-1 py-0.5">WINDOWS_PRINTER_NAME</code>.
                  Desde el POS solo necesitas la URL del printer-server.
                </div>

                <label className="flex items-center gap-3 rounded-lg bg-white px-4 py-3 border border-gray-200">
                  <input
                    type="checkbox"
                    checked={Boolean(localSettings?.printing?.autoPrintSales)}
                    onChange={(e) => handlePrintingChange('autoPrintSales', e.target.checked)}
                  />
                  <span className="text-sm font-medium text-gray-700">Impresion automatica de ventas</span>
                </label>

                <label className="flex items-center gap-3 rounded-lg bg-white px-4 py-3 border border-gray-200">
                  <input
                    type="checkbox"
                    checked={Boolean(localSettings?.printing?.autoPrintKitchen)}
                    onChange={(e) => handlePrintingChange('autoPrintKitchen', e.target.checked)}
                  />
                  <span className="text-sm font-medium text-gray-700">Impresion automatica de cocina</span>
                </label>

                <label className="md:col-span-2 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={localPrintStation}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setLocalPrintStation(enabled);
                      setPrintStationEnabled(enabled);
                    }}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-emerald-900">Este PC es la estacion de impresion</span>
                    <span className="mt-1 block text-xs text-emerald-800">
                      Activalo solamente en el computador principal conectado a la impresora. Los pedidos y tickets
                      de los meseros llegaran por Firebase y este PC los enviara a http://localhost:3001.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={handleSaveSettings}
              disabled={savingSettings}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {savingSettings ? 'Guardando...' : 'Guardar Configuracion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GlobalSettingsView;
